import { argon2id } from 'hash-wasm';
import { importAesKey, randomBytes } from './primitives.ts';

/**
 * Schlüsselableitung aus dem Dokumenten-Zugriffspasswort (Konzept 3.2).
 *
 * Argon2id, weil es als einziges gängiges Verfahren nennenswert
 * speicherintensiv ist und damit Angriffe mit GPUs oder Spezialhardware
 * teuer macht. Web Crypto bietet nur PBKDF2 – das wäre hier zu schwach.
 *
 * Das Ergebnis verlässt den Browser niemals. Der Server sieht ausschließlich
 * den damit verschlüsselten Private Key.
 */

export interface KdfParams {
  /** Speicherbedarf in KiB. */
  m: number;
  /** Durchläufe. */
  t: number;
  /** Parallelität. */
  p: number;
  version: number;
}

/**
 * 64 MiB / 3 Durchläufe: spürbar, aber auf einem älteren Notebook oder
 * Tablet noch im Bereich von etwa einer Sekunde. Die Zielgruppe sitzt nicht
 * zwingend an schneller Hardware; ein Login, der zehn Sekunden hängt, würde
 * als Defekt wahrgenommen.
 */
export const DEFAULT_KDF_PARAMS: KdfParams = { m: 65_536, t: 3, p: 1, version: 19 };

export const SALT_BYTES = 16;

export function generateSalt(): Uint8Array {
  return randomBytes(SALT_BYTES);
}

// ---------------------------------------------------------------------------
// Ein Passwort, zwei getrennte Ableitungen
// ---------------------------------------------------------------------------

/**
 * Der Kunde gibt genau ein Passwort ein. Daraus entstehen im Browser zwei
 * Werte, die sich nicht auseinander zurückrechnen lassen:
 *
 *   Passwort ──Argon2id(Salz aus E-Mail)──► Hauptschlüssel (verlässt nie das Gerät)
 *                                            │
 *                    ┌───────────────────────┴───────────────────────┐
 *                    ▼                                               ▼
 *          HKDF(info="anmeldung")                        HKDF(info="verschluesselung")
 *                    │                                               │
 *            Anmeldebeweis                                 Verschlüsselungsschlüssel
 *        (geht an den Server)                            (bleibt auf dem Gerät)
 *
 * Der Server erhält ausschließlich den Anmeldebeweis und speichert davon
 * nur einen weiteren Hash. Selbst mit vollem Datenbankzugriff kommt er damit
 * weder an das Passwort noch an den Verschlüsselungsschlüssel – das ist der
 * Punkt, an dem Zero-Knowledge trotz einer einzigen Eingabe erhalten bleibt.
 *
 * Dasselbe Verfahren nutzt Bitwarden/Vaultwarden für sein Master-Passwort.
 */

/**
 * Das Salz wird aus der E-Mail-Adresse abgeleitet statt zufällig gewählt.
 *
 * Grund: Es muss schon VOR der Anmeldung feststehen, denn der Anmeldebeweis
 * entsteht ja erst daraus. Ein serverseitig abgelegtes Zufallssalz müsste
 * unangemeldet abrufbar sein – das wäre eine Auskunftsstelle darüber, welche
 * E-Mail-Adressen Kunden sind.
 *
 * Der Zusatz vor der Adresse trennt die Ableitung von anderen Systemen, die
 * womöglich ebenfalls die E-Mail als Salz verwenden.
 */
export async function deriveSaltFromEmail(email: string): Promise<Uint8Array> {
  const material = new TextEncoder().encode(
    `it-karsten-fischer/dokumentensystem/kdf/v2|${email.trim().toLowerCase()}`,
  );
  const digest = await crypto.subtle.digest('SHA-256', material as BufferSource);
  return new Uint8Array(digest).slice(0, SALT_BYTES);
}

/** Der Hauptschlüssel selbst wird nie verwendet – nur die beiden Ableitungen. */
export async function deriveMasterKey(
  email: string,
  password: string,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<Uint8Array> {
  return argon2id({
    password,
    salt: await deriveSaltFromEmail(email),
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: 32,
    outputType: 'binary',
  });
}

async function hkdf(masterKey: Uint8Array, info: string, bits = 256): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', masterKey as BufferSource, 'HKDF', false, [
    'deriveBits',
  ]);
  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      // Salz hier leer: Die Entropie steckt bereits im Hauptschlüssel, der
      // aus Argon2id stammt. `info` trennt die beiden Verwendungszwecke.
      salt: new Uint8Array(0) as BufferSource,
      info: new TextEncoder().encode(info) as BufferSource,
    },
    key,
    bits,
  );
  return new Uint8Array(derived);
}

/**
 * Wird an den Server geschickt und tritt dort an die Stelle des Passworts.
 * Base64, damit er durch dieselben Wege wie ein Passwort transportiert werden
 * kann.
 */
export async function deriveAuthSecret(masterKey: Uint8Array): Promise<string> {
  const bytes = await hkdf(masterKey, 'it-karsten-fischer/anmeldung/v2');
  return btoa(String.fromCharCode(...bytes));
}

/** Verpackt den privaten Schlüssel. Verlässt das Gerät niemals. */
export async function deriveEncryptionKey(masterKey: Uint8Array): Promise<CryptoKey> {
  return importAesKey(await hkdf(masterKey, 'it-karsten-fischer/verschluesselung/v2'), [
    'encrypt',
    'decrypt',
  ]);
}

/**
 * Leitet aus Passwort und Salt einen AES-Schlüssel ab, mit dem der private
 * Schlüssel des Kunden ver- und entschlüsselt wird.
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<CryptoKey> {
  const raw = await argon2id({
    password,
    salt,
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: 32,
    outputType: 'binary',
  });
  return importAesKey(raw, ['encrypt', 'decrypt']);
}

// ---------------------------------------------------------------------------
// Wiederherstellungscode (Konzept 3.6)
// ---------------------------------------------------------------------------

/**
 * Wortliste für den Recovery-Code. Bewusst kurze, eindeutig schreibbare
 * deutsche Wörter ohne Umlaute – der Code wird ausgedruckt und später
 * abgetippt, oft von jemandem, der nicht schnell tippt.
 */
const RECOVERY_WORDS = [
  'anker', 'apfel', 'atlas', 'bahn', 'baum', 'berg', 'blatt', 'blume',
  'boden', 'brief', 'bruecke', 'buch', 'dach', 'daumen', 'delfin', 'donner',
  'eiche', 'eimer', 'eisen', 'engel', 'ernte', 'esel', 'fahne', 'feder',
  'feld', 'fenster', 'feuer', 'fisch', 'flasche', 'fluss', 'garten', 'gabel',
  'gipfel', 'glocke', 'hafen', 'hammer', 'hase', 'haus', 'herbst', 'himmel',
  'honig', 'hufe', 'hund', 'insel', 'jacke', 'jahr', 'kabel', 'kaffee',
  'kamin', 'karte', 'kerze', 'kiesel', 'kirsche', 'knopf', 'koffer', 'korb',
  'kranz', 'kreide', 'krone', 'kueche', 'lampe', 'laub', 'leiter', 'licht',
  'lied', 'loeffel', 'luft', 'mandel', 'mantel', 'markt', 'mauer', 'meer',
  'messer', 'mond', 'motor', 'muehle', 'nadel', 'nebel', 'nest', 'norden',
  'ofen', 'orgel', 'palme', 'panda', 'papier', 'pfeil', 'pflaume', 'pilz',
  'platte', 'quelle', 'rabe', 'rahmen', 'regen', 'reifen', 'ring', 'rose',
  'ruder', 'sand', 'schere', 'schiff', 'schnee', 'schuh', 'segel', 'seife',
  'sessel', 'sommer', 'sonne', 'spiegel', 'stein', 'stern', 'strand', 'stuhl',
  'tafel', 'tanne', 'tasse', 'taube', 'teller', 'teppich', 'tiger', 'tisch',
  'traube', 'treppe', 'trommel', 'tuch', 'turm', 'ufer', 'uhr', 'vogel',
  'wagen', 'wald', 'wasser', 'weg', 'welle', 'wiese', 'wind', 'winter',
  'wolke', 'wurzel', 'zange', 'zaun', 'zebra', 'ziegel', 'zimmer', 'zug',
] as const;

const RECOVERY_WORD_COUNT = 12;

/**
 * Erzeugt einen Wiederherstellungscode. 12 Wörter aus 144 ergeben rund
 * 86 Bit Entropie – genug, um nicht erraten zu werden, und kurz genug, um
 * ihn auf einen Zettel zu schreiben.
 *
 * Wir speichern diesen Code nirgends. Geht er verloren und ist auch
 * das Passwort weg, sind die Dokumente endgültig nicht mehr lesbar.
 */
export function generateRecoveryCode(): string {
  const indices = new Uint32Array(RECOVERY_WORD_COUNT);
  crypto.getRandomValues(indices);
  return Array.from(indices, (value) => RECOVERY_WORDS[value % RECOVERY_WORDS.length])
    .join(' ');
}

/** Toleriert Groß-/Kleinschreibung und beliebige Abstände beim Abtippen. */
export function normalizeRecoveryCode(code: string): string {
  return code.trim().toLowerCase().split(/\s+/).join(' ');
}

export async function deriveKeyFromRecoveryCode(
  code: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  return deriveKeyFromPassword(normalizeRecoveryCode(code), salt);
}

/**
 * Der Wiederherstellungscode wird genauso behandelt wie das Passwort: Aus ihm
 * entstehen ein Nachweis für den Server und ein Schlüssel, der das Gerät nie
 * verlässt.
 *
 * Ohne diesen Nachweis könnte jeder, der eine Kundenadresse kennt, den
 * Wiederherstellungsweg auslösen und dabei den verschlüsselten privaten
 * Schlüssel überschreiben – die Dokumente wären damit unwiederbringlich
 * verloren, ohne dass der Angreifer sie je hätte lesen können. Reine
 * Zerstörung, aber die will man auch nicht.
 */
export async function deriveRecoveryMasterKey(
  email: string,
  code: string,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<Uint8Array> {
  const material = new TextEncoder().encode(
    `it-karsten-fischer/dokumentensystem/wiederherstellung/v2|${email.trim().toLowerCase()}`,
  );
  const digest = await crypto.subtle.digest('SHA-256', material as BufferSource);
  return argon2id({
    password: normalizeRecoveryCode(code),
    salt: new Uint8Array(digest).slice(0, SALT_BYTES),
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: 32,
    outputType: 'binary',
  });
}

export async function deriveRecoveryAuthSecret(masterKey: Uint8Array): Promise<string> {
  const bytes = await hkdf(masterKey, 'it-karsten-fischer/wiederherstellung-anmeldung/v2');
  return btoa(String.fromCharCode(...bytes));
}

export async function deriveRecoveryEncryptionKey(masterKey: Uint8Array): Promise<CryptoKey> {
  return importAesKey(await hkdf(masterKey, 'it-karsten-fischer/wiederherstellung-schluessel/v2'), [
    'encrypt',
    'decrypt',
  ]);
}

// ---------------------------------------------------------------------------
// Einladungsgeheimnis (Schlüsselkreise, Migration 007/012)
// ---------------------------------------------------------------------------

/**
 * Ein Einladungsgeheimnis ist bereits ein voll zufälliger, hochentropischer
 * Wert (`crypto.getRandomValues`, 32 Byte) — anders als ein Passwort oder ein
 * zum Eintippen gedachter Wiederherstellungscode braucht es kein Argon2id zum
 * Strecken. Direktes HKDF genügt, mit einem eigenen `info`-String, damit
 * derselbe Mechanismus für andere Zwecke einen anderen Schlüssel ergäbe.
 */
export async function deriveInviteKey(secret: Uint8Array): Promise<CryptoKey> {
  return importAesKey(await hkdf(secret, 'it-karsten-fischer/dokumentensystem/einladung/v1'), [
    'encrypt',
    'decrypt',
  ]);
}

export function generateInviteSecret(): Uint8Array {
  return randomBytes(32);
}

// ---------------------------------------------------------------------------
// Kreisbezeichnung (Schlüsselkreise, Migration 007)
// ---------------------------------------------------------------------------

/**
 * Name/Farbe/Kurztext eines Kreises werden mit einem aus dem Kreis-eigenen
 * Schlüsselpaar abgeleiteten AES-Schlüssel verschlüsselt — ECDH selbst
 * verschlüsselt keine Daten, nur der private Schlüssel ist als Rohmaterial
 * vorhanden (vor dem `crypto.subtle.importKey`, das ihn nicht-exportierbar
 * macht). Wer den Kreis über die Umschlagkette öffnen kann, hat diese Bytes
 * ohnehin schon durchlaufen — kein zusätzlicher Umschlag nötig.
 */
export async function deriveKreisBezeichnungsSchluessel(
  privateKeyPkcs8: Uint8Array,
): Promise<CryptoKey> {
  return importAesKey(
    await hkdf(privateKeyPkcs8, 'it-karsten-fischer/dokumentensystem/kreis-bezeichnung/v1'),
    ['encrypt', 'decrypt'],
  );
}
